import { EnvironmentOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Card, Table } from 'antd';
import { DonorStatus } from 'gql/types';
import { FC, useMemo } from 'react';

import {
  ILocationEntry,
  LOCATION_COLUMNS,
  LOCATION_CSV_EXTRACTORS,
} from './locationColumns';

import { GET_RECRUITER_LOCATION_STATS_QUERY } from '../../../../graphql/queries';
import { ITabViewProps } from '../../types';
import { downloadCsv, makeCsvFilename } from '../../utils/csvUtils';
import DonorDetailsHeader from '../DonorDetailsHeader';
import TableToolbar from '../TableToolbar';

interface IRecruiterLocationsViewProps extends ITabViewProps {
  recruiterId: string | null;
  activeDonorStatuses: DonorStatus[] | undefined;
}

export const RecruiterLocationsView: FC<IRecruiterLocationsViewProps> = ({
  recruiterId,
  activeDonorStatuses,
  meta,
  onBack,
  onLoadActiveDonors,
}) => {
  const { data, loading } = useQuery(GET_RECRUITER_LOCATION_STATS_QUERY, {
    variables: {
      input: {
        recruiterId: recruiterId || '',
        activeDonorStatuses: activeDonorStatuses?.length
          ? activeDonorStatuses
          : undefined,
      },
    },
    skip: !recruiterId,
  });

  const entries: ILocationEntry[] = useMemo(
    () => data?.getRecruiterLocationStats?.entries ?? [],
    [data]
  );

  const handleExport = () => {
    downloadCsv(
      LOCATION_COLUMNS.map((c) => String(c.title)),
      entries.map((e) =>
        LOCATION_COLUMNS.map(
          (c) => LOCATION_CSV_EXTRACTORS[String(c.key)]?.(e) ?? ''
        )
      ),
      makeCsvFilename('recruiter-locations')
    );
  };

  return (
    <div style={{ padding: 0 }}>
      <DonorDetailsHeader
        title={meta.title}
        description={meta.description}
        iconColor={meta.iconColor}
        donorCount={entries.length}
        countLabel="locations"
        loading={loading}
        onBack={onBack}
        onLoadActiveDonors={onLoadActiveDonors}
      />
      <Card bodyStyle={{ padding: '16px 20px' }}>
        <TableToolbar
          iconColor={meta.iconColor}
          title="Location List"
          exportDisabled={!entries.length}
          onExport={handleExport}
          icon={
            <EnvironmentOutlined
              style={{ fontSize: 18, color: meta.iconColor }}
            />
          }
        />
        <Table
          rowKey="locationId"
          columns={LOCATION_COLUMNS}
          dataSource={entries}
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            defaultPageSize: 50,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (t, range) =>
              `${range[0]}-${range[1]} of ${t} locations`,
          }}
          size="small"
          style={{ marginTop: 8 }}
        />
      </Card>
    </div>
  );
};

export default RecruiterLocationsView;
